import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  Cell,
} from "recharts";
import { PlaneTakeoff, ShipWheel, ShieldCheck, PackageCheck, Loader2 } from "lucide-react";
import { useAnalytics } from "@/lib/useAnalytics";
import { KpiCard } from "@/components/KpiCard";
import { ChartCard, SectionTitle } from "@/components/ChartCard";
import { fmtInt, fmtPct, monthLabel, CHART, tooltipStyle } from "@/lib/format";

const tone = (v) => (v >= 85 ? "good" : v >= 70 ? "warn" : "bad");
const REASON_COLORS = [CHART.sea, CHART.air, CHART.road, CHART.lcl, CHART.cfs, CHART.bad, CHART.warn, CHART.ok];

const Operational = () => {
  const { data, isLoading } = useAnalytics("operational", "/analytics/operational");
  if (isLoading || !data)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  const k = data.kpis;
  const trend = data.trend.map((t) => ({ ...t, label: monthLabel(t.month) }));

  return (
    <div>
      <SectionTitle
        title="Operational Management"
        description="On-time performance across departure, arrival, customs and delivery — with delay analysis"
      />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        <KpiCard testId="kpi-otdep" label="On-Time Departure" value={fmtPct(k.on_time_departure)} icon={PlaneTakeoff} tone={tone(k.on_time_departure)} info="% of shipments that departed on schedule against the required delivery date." />
        <KpiCard testId="kpi-otarr" label="On-Time Arrival" value={fmtPct(k.on_time_arrival)} icon={ShipWheel} tone={tone(k.on_time_arrival)} info="% of shipments that arrived at the destination port/airport on or before ETA." />
        <KpiCard testId="kpi-otcust" label="On-Time Customs" value={fmtPct(k.on_time_customs)} icon={ShieldCheck} tone={tone(k.on_time_customs)} info="% of shipments cleared through customs within the target time." />
        <KpiCard testId="kpi-otdel" label="On-Time Delivery" value={fmtPct(k.on_time_delivery)} icon={PackageCheck} tone={tone(k.on_time_delivery)} info="% of shipments delivered on or before the customer's Required Delivery Date." />
        <KpiCard testId="kpi-delays" label="Total Delays" value={fmtInt(k.total_delays)} icon={PackageCheck} tone={k.total_delays === 0 ? "good" : "warn"} info="Shipments flagged with a transit exception (missed arrival or delivery), each tagged with a reason code." />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <ChartCard testId="chart-op-trend" title="On-Time Performance Trend" subtitle="Monthly % against required dates" className="lg:col-span-2">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trend} margin={{ left: -18, right: 8, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="label" tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
              <YAxis domain={[0, 100]} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend iconType="plainline" wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="on_time_departure" name="Departure" stroke={CHART.sea} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="on_time_arrival" name="Arrival" stroke={CHART.air} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="on_time_customs" name="Customs" stroke={CHART.road} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="on_time_delivery" name="Delivery" stroke={CHART.lcl} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard testId="chart-delay-reasons" title="Delays by Reason Code" subtitle="Root-cause breakdown">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={data.reason_breakdown} layout="vertical" margin={{ left: 40, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
              <XAxis type="number" tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
              <YAxis type="category" dataKey="reason" width={120} tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" style={{ fontSize: 11 }} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "hsl(var(--secondary))" }} />
              <Bar dataKey="count" radius={[0, 3, 3, 0]} barSize={16}>
                {data.reason_breakdown.map((_, i) => (
                  <Cell key={i} fill={REASON_COLORS[i % REASON_COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="mt-6 overflow-hidden rounded-xl border border-border bg-card card-elevated">
        <div className="border-b border-border/70 px-5 py-4">
          <h3 className="text-sm font-semibold tracking-tight">Delayed Shipments</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">{data.delayed_shipments.length} shipments flagged against required delivery</p>
        </div>
        <div className="max-h-[420px] overflow-auto">
          <table className="w-full text-sm" data-testid="delayed-shipments-table">
            <thead className="sticky top-0 bg-card">
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="px-5 py-3 font-medium">Supplier</th>
                <th className="px-5 py-3 font-medium">Mode</th>
                <th className="px-5 py-3 font-medium">Lane</th>
                <th className="px-5 py-3 font-medium">Required</th>
                <th className="px-5 py-3 font-medium">Delivered</th>
                <th className="px-5 py-3 font-medium">Reason</th>
              </tr>
            </thead>
            <tbody>
              {data.delayed_shipments.map((r, i) => (
                <tr key={i} className="border-b border-border/60 transition-colors hover:bg-secondary/50">
                  <td className="px-5 py-2.5 font-medium">{r.shipper}</td>
                  <td className="px-5 py-2.5">{r.mode}</td>
                  <td className="px-5 py-2.5 text-muted-foreground">{r.origin} → {r.destination}</td>
                  <td className="px-5 py-2.5 font-mono">{r.required_delivery}</td>
                  <td className="px-5 py-2.5 font-mono">{r.delivery_date}</td>
                  <td className="px-5 py-2.5">
                    <span className="rounded-sm bg-red-50 px-2 py-0.5 text-xs text-red-700">{r.reason}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Operational;
